import { cn } from '@/utils/shadcn'
import useMessageStore from '@/stores/useMessageStore'
import { forwardRef, useEffect } from 'react'
import { getNextPageParam, useInfiniteQueryGraphQL, useMutationGraphQL } from '@/use-graphql'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Loader2 } from 'lucide-react'
import Loading from '@/components/Loading'
import { ErrorMessage } from '@/components/ErrorMessage'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary } from '@sentry/react'
import { messagesQuery } from '@/pages/messages/graphql/query'
import { readChatMutation } from '@/pages/messages/graphql/mutation'
import MessageItem from './MessageItem'
import HelpMessageList from './components/HelpMessageList'
import useAuthStore from '@/stores/useAuthStore'

const MESSAGE_LIMIT = 15

const MessageList = forwardRef<HTMLDivElement, { className?: string }>(({ className }, ref) => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { activeChat, setActiveChat } = useMessageStore()
  const { data, fetchNextPage, hasNextPage, status, error } = useInfiniteQueryGraphQL(
    ['messages'],
    messagesQuery,
    {
      chatId: activeChat?.id || '',
      pagination: { limit: MESSAGE_LIMIT },
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        return getNextPageParam({
          allPages,
          limit: MESSAGE_LIMIT,
          totalCount: lastPage?.chat?.messages.totalItemsCount,
        })
      },
      enabled: !!activeChat?.id,
      refetchInterval: 3000,
      staleTime: 1000,
    },
  )
  const readChatMutate = useMutationGraphQL(readChatMutation, { chatId: activeChat?.id || '' })

  useEffect(() => {
    if (activeChat) {
      readChatMutate.mutate()
    }
    return () => {
      activeChat && readChatMutate.mutate()
    }
  }, [activeChat?.id])

  useEffect(() => {
    return () => {
      setTimeout(() => {
        setActiveChat(null)
      }, 500)
    }
  }, [])

  if (!activeChat) {
    return <div className="h-[calc(100%-50px)] md:h-[calc(100%-90px)]" />
  }
  if (status === 'loading') {
    return <Loading />
  }
  if (status === 'error') {
    // @ts-ignore
    return <ErrorMessage message={error?.message} />
  }
  if (data === undefined) {
    return <h5 className="my-10 text-center text-2xl text-brand-torea">{t('title.no_data')}</h5>
  }

  const dataLength = data.pages.reduce((counter, page) => {
    return counter + page.chat.messages.totalItemsCount || 0
  }, 0)

  return (
    <ErrorBoundary fallback={<ErrorMessage />}>
      <div
        className={cn(
          'flex h-[calc(100%-50px)] flex-col-reverse gap-4 overflow-auto px-4 pt-2 md:h-[calc(100%-90px)] ',
          className,
        )}
        ref={ref}
        id="scrollbar-target"
      >
        {dataLength > 0 ? (
          <InfiniteScroll
            dataLength={dataLength}
            next={fetchNextPage}
            inverse
            scrollThreshold={0.7}
            hasMore={!!hasNextPage}
            loader={<Loader2 size={30} className="mx-auto my-8 animate-spin" />}
            scrollableTarget="scrollbar-target"
            style={{ display: 'flex', flexDirection: 'column-reverse', gap: 12, paddingTop: 8 }}
          >
            {data?.pages.map((group) =>
              group.chat.messages?.items?.map((e) => (
                <MessageItem
                  isSender={e.direction === 'MessageDirection.OUT'}
                  key={e.id}
                  text={e.content}
                  date={e.createdAt}
                />
              )),
            )}
            <MessageItem
              isSender={false}
              key={-1}
              text={`Hi ${user?.firstName || ''}! ${t('label.customer_support_help_question')}`}
              date={''}
            />
          </InfiniteScroll>
        ) : (
          <HelpMessageList chatId={activeChat.id} />
        )}
      </div>
    </ErrorBoundary>
  )
})

export default MessageList
